/**
 * Base JS for Foundation theme
 * Implements native-first interactive components.
 */

// Add .js class to <html> if JavaScript is enabled
document.documentElement.className = document.documentElement.className.replace('no-js', 'js');

/**
 * Custom Element: Quantity Input
 * Handles increment/decrement buttons for quantity inputs.
 */
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    // Event Delegation directly on the component
    this.addEventListener('click', (event) => {
      if (event.target.tagName === 'BUTTON' || event.target.closest('button')) {
        this.onButtonClick(event);
      }
    });

    this.validateButtons();
    this.input.readOnly = true;
  }

  onButtonClick(event) {
    event.preventDefault();
    const button = event.target.tagName === 'BUTTON' ? event.target : event.target.closest('button');
    const previousValue = parseInt(this.input.value, 10);

    if (button.name === 'plus') {
      this.input.stepUp();
    } else if (previousValue <= 1) {
      this.input.dataset.pendingQuantity = '0';
      this.input.dispatchEvent(this.changeEvent);
      return;
    } else {
      this.input.stepDown();
    }

    const nextValue = parseInt(this.input.value, 10);

    if (previousValue !== nextValue) {
      this.input.dispatchEvent(this.changeEvent);
      this.validateButtons();
    }
  }

  validateButtons() {
    const value = parseInt(this.input.value, 10);
    const max = parseInt(this.input.max) || Infinity;
    const buttons = this.querySelectorAll('button');

    for (const button of buttons) {
      if (button.name === 'plus') {
        button.disabled = value >= max;
      } else if (button.name === 'minus') {
        const iconMinus = button.querySelector('.icon-minus');
        const iconTrash = button.querySelector('.icon-trash');

        if (iconMinus && iconTrash) {
          const isAtOne = value === 1;
          iconMinus.classList.toggle('hidden', isAtOne);
          iconTrash.classList.toggle('hidden', !isAtOne);
        }
      }
    }
  }
}

customElements.define('quantity-input', QuantityInput);

// Handle cart update for No-JS fallback
document.addEventListener('change', (event) => {
  if (event.target.classList.contains('quantity__input') && event.target.closest('.cart__form')) {
    // In a real sophisticated theme, we'd use Fetch API to update cart.
    // For a high-performance minimalist theme, we can just submit the form.
    event.target.closest('form').submit();
  }
});

/* Global Utilities */
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Fetches a URL and returns the parsed HTML Document.
 * @param {string} url - The URL to fetch.
 * @returns {Promise<Document>}
 */
function fetchHTML(url) {
  return fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return response.text();
    })
    .then((text) => new DOMParser().parseFromString(text, 'text/html'));
}

/**
 * Copies the header cart count bubble into the mobile dock (header icon is hidden on small screens).
 */
function syncDockCartCountBubble() {
  const sourceBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
  const dockBubble = document.querySelector('#DockCartTrigger .cart-count-bubble');

  if (!sourceBubble || !dockBubble) return;

  dockBubble.replaceWith(sourceBubble.cloneNode(true));
}

/**
 * @param {string} cartDrawerSectionHtml
 */
function replaceCartDrawerContentFromSectionHtml(cartDrawerSectionHtml) {
  if (!cartDrawerSectionHtml) return;

  const sectionDoc = new DOMParser().parseFromString(cartDrawerSectionHtml, 'text/html');
  const targetElement = document.querySelector('#CartDrawer-Content');
  const sourceElement = sectionDoc.querySelector('#CartDrawer-Content');

  if (targetElement && sourceElement) {
    targetElement.replaceWith(sourceElement);
  }
}

/**
 * Updates cart drawer markup and defers header cart count until fly-to-cart completes when applicable.
 * @param {object} response
 * @param {{
 *   error?: boolean,
 *   flySession?: { succeed: (fn?: () => void) => void } | null,
 *   flySessionPromise?: Promise<{ succeed: (fn?: () => void) => void } | null> | null,
 *   hostEl?: Element,
 * }} [options]
 * @returns {(() => void) | null} Pending cart count callback when fly session is still starting
 */
function scheduleCartSectionsUpdateFromResponse(response, options = {}) {
  if (!response?.sections) return null;

  const cartDrawer = document.querySelector('cart-drawer');

  if (cartDrawer && response.sections['cart-drawer']) {
    replaceCartDrawerContentFromSectionHtml(response.sections['cart-drawer']);
  }

  const headerSectionHtml = response.sections.header;
  const updateCartCount = () => {
    replaceCartIconBubbleFromHeaderSection(headerSectionHtml);
  };

  if (options.error) {
    return null;
  }

  const { flySession, flySessionPromise, hostEl } = options;

  if (flySession) {
    flySession.succeed(updateCartCount);
    return null;
  }

  if (flySessionPromise) {
    flySessionPromise.then((session) => {
      if (!session && headerSectionHtml) {
        updateCartCount();
      }
    });
    return updateCartCount;
  }

  if (hostEl && typeof window.flyProductImageToCart === 'function') {
    window.flyProductImageToCart(hostEl, { onCartBump: updateCartCount }).then((flyScheduled) => {
      if (!flyScheduled && headerSectionHtml) {
        updateCartCount();
      }
    });
    return null;
  }

  if (headerSectionHtml) {
    updateCartCount();
  }

  return null;
}

/**
 * Replaces #cart-icon-bubble from rendered header section HTML and syncs the dock badge.
 * @param {string} headerSectionHtml
 */
function replaceCartIconBubbleFromHeaderSection(headerSectionHtml) {
  if (!headerSectionHtml) return;

  const doc = new DOMParser().parseFromString(headerSectionHtml, 'text/html');
  const headerIcon = document.querySelector('#cart-icon-bubble');
  const newHeaderIcon = doc.querySelector('#cart-icon-bubble');

  if (!headerIcon || !newHeaderIcon) return;

  headerIcon.replaceWith(newHeaderIcon);
  syncDockCartCountBubble();
}


/* Lazy Video Loading */
document.addEventListener('DOMContentLoaded', () => {
  // Only target videos with the lazy-video class
  const lazyVideos = document.querySelectorAll('video.lazy-video');

  if ('IntersectionObserver' in window) {
    const videoObserver = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          // If video is in view
          if (entry.isIntersecting) {
            const video = entry.target;

            // If it has data-autoplay attribute, play it
            if (video.hasAttribute('data-autoplay')) {
              video.play().catch(() => {});
            }

            // Stop observing once loaded/playing
            observer.unobserve(video);
          } else {
            // Pause video when out of view to save resources
            const video = entry.target;
            if (!video.paused) {
              video.pause();
            }
          }
        }
      },
      {
        rootMargin: '50px 0px', // Start loading/playing shortly before it comes into view
        threshold: 0.1,
      },
    );

    for (const video of lazyVideos) {
      videoObserver.observe(video);
    }
  } else {
    // Fallback for very old browsers: just play them
    for (const video of lazyVideos) {
      if (video.hasAttribute('data-autoplay')) {
        video.play();
      }
    }
  }
});

/* Desktop Mega Menu Hover Interaction */
document.addEventListener('DOMContentLoaded', () => {
  const details = document.querySelectorAll('details.mega-menu');
  for (const detail of details) {
    detail.addEventListener('mouseenter', () => {
      detail.setAttribute('open', 'true');
    });

    detail.addEventListener('mouseleave', () => {
      detail.removeAttribute('open');
    });
  }
});

/* Footer Accordion Interaction */
document.addEventListener('DOMContentLoaded', () => {
  const accordions = document.querySelectorAll('.footer__menu-accordion');

  function setAccordionState() {
    const isDesktop = window.matchMedia('(width >= 60em)').matches;
    for (const acc of accordions) {
      if (isDesktop) {
        acc.setAttribute('open', '');
      } else {
        // Conventionally on mobile resize, usually close them
        acc.removeAttribute('open');
      }
    }
  }

  setAccordionState();
  window.addEventListener('resize', setAccordionState);
});
